import { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, Clock, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/i18n/LanguageContext";
import PageHeader from "@/components/layout/PageHeader";
import Reveal from "@/components/motion/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full border border-line bg-panel px-4 py-3.5 text-sm text-cream placeholder:text-ash outline-none transition-colors focus:border-brand";
const labelCls = "mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-fog";

const Contact = () => {
  const { t } = useLanguage();
  const c = t.contact;
  const waLink = `https://wa.me/${t.common.whatsappNumber}?text=${encodeURIComponent(t.whatsapp.message)}`;
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
    } catch (err) {
      toast.error(c.errorText);
    } finally {
      setSending(false);
    }
  };

  const info = [
    { icon: Phone, label: c.phone, value: t.common.phone, href: `tel:${t.common.phoneRaw}`, testid: "contact-phone" },
    {
      icon: () => (
        <svg viewBox="0 0 24 24" className="h-5 w-5 fill-brand" aria-hidden="true">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
        </svg>
      ),
      label: c.whatsapp, value: t.common.phone, href: waLink, testid: "contact-whatsapp",
    },
    { icon: Mail, label: c.email, value: t.common.email, href: `mailto:${t.common.email}`, testid: "contact-email" },
    { icon: MapPin, label: c.address, value: t.common.address, testid: "contact-address" },
    { icon: Clock, label: c.hours, value: c.hoursValue, testid: "contact-hours" },
  ];

  return (
    <>
      <PageHeader chapter={c.chapter} title={c.title} subtitle={c.subtitle} testid="contact-header" />

      <section className="bg-ink py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <h2 className="font-mono text-xs uppercase tracking-[0.25em] text-brand">{c.infoTitle}</h2>
              <div className="mt-6 space-y-px border border-line bg-line">
                {info.map((item) => {
                  const Icon = item.icon;
                  const content = (
                    <div className="flex items-start gap-4 bg-panel p-5 transition-colors hover:bg-[#20262e]">
                      <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center border border-line">
                        <Icon className="h-5 w-5 text-brand" />
                      </span>
                      <div>
                        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ash">{item.label}</p>
                        <p className="mt-1 text-sm text-cream">{item.value}</p>
                      </div>
                    </div>
                  );
                  return item.href ? (
                    <a key={item.label} href={item.href} data-testid={item.testid} target={item.href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer">
                      {content}
                    </a>
                  ) : (
                    <div key={item.label} data-testid={item.testid}>{content}</div>
                  );
                })}
              </div>
              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <a
                  href={`tel:${t.common.phoneRaw}`}
                  data-testid="contact-call-button"
                  className="flex flex-1 items-center justify-center gap-3 bg-brand px-6 py-4 font-display text-base font-bold uppercase tracking-[0.12em] text-ink transition-colors hover:bg-brand-hover"
                >
                  <Phone className="h-4 w-4" /> {c.callNow}
                </a>
                <a
                  href={waLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-whatsapp-button"
                  className="flex flex-1 items-center justify-center gap-3 border border-[#25D366]/60 px-6 py-4 font-display text-base font-bold uppercase tracking-[0.12em] text-[#25D366] transition-colors hover:bg-[#25D366] hover:text-ink"
                >
                  {c.whatsappCta}
                </a>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <h2 className="font-mono text-xs uppercase tracking-[0.25em] text-brand">{c.formTitle}</h2>
              {sent ? (
                <div data-testid="contact-success-panel" className="mt-6 border border-brand/40 bg-panel p-10 text-center">
                  <CheckCircle2 className="mx-auto h-12 w-12 text-brand" strokeWidth={1.25} />
                  <h3 className="mt-5 font-display text-2xl font-bold uppercase text-cream">{c.successTitle}</h3>
                  <p className="mt-2 text-sm text-fog">{c.successText}</p>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="contact-section-form" className="mt-6 border border-line bg-surface p-6 sm:p-8">
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                      <label className={labelCls}>{c.fields.name} <span className="text-brand">*</span></label>
                      <input required value={form.name} onChange={set("name")} placeholder={c.placeholders.name} className={inputCls} data-testid="contact-input-name" />
                    </div>
                    <div>
                      <label className={labelCls}>{c.fields.email} <span className="text-brand">*</span></label>
                      <input required type="email" value={form.email} onChange={set("email")} placeholder={c.placeholders.email} className={inputCls} data-testid="contact-input-email" />
                    </div>
                    <div>
                      <label className={labelCls}>{c.fields.phone}</label>
                      <input type="tel" value={form.phone} onChange={set("phone")} placeholder={c.placeholders.phone} className={inputCls} data-testid="contact-input-phone" />
                    </div>
                    <div>
                      <label className={labelCls}>{c.fields.subject} <span className="text-brand">*</span></label>
                      <input required value={form.subject} onChange={set("subject")} placeholder={c.placeholders.subject} className={inputCls} data-testid="contact-input-subject" />
                    </div>
                    <div className="sm:col-span-2">
                      <label className={labelCls}>{c.fields.message} <span className="text-brand">*</span></label>
                      <textarea required rows={6} value={form.message} onChange={set("message")} placeholder={c.placeholders.message} className={`${inputCls} resize-y`} data-testid="contact-textarea-message" />
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={sending}
                    data-testid="contact-form-submit-button"
                    className="mt-7 flex w-full sm:w-auto items-center justify-center gap-3 bg-brand px-10 py-4 font-display text-lg font-bold uppercase tracking-[0.12em] text-ink transition-colors hover:bg-brand-hover disabled:opacity-60"
                  >
                    {sending && <Loader2 className="h-5 w-5 animate-spin" />}
                    {sending ? c.sending : c.submit}
                  </button>
                </form>
              )}
            </Reveal>
          </div>
        </div>
      </section>

      <section data-testid="contact-map-section" className="border-t border-line bg-surface">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14">
          <Reveal>
            <h2 className="font-mono text-xs uppercase tracking-[0.25em] text-brand">{c.mapTitle}</h2>
            <div className="mt-6 overflow-hidden border border-line">
              <iframe
                title="OMNIBÂT — Lomé Adidogomé Yokoé, Togo"
                data-testid="contact-map-iframe"
                src="https://www.google.com/maps?q=Adidogom%C3%A9,+Lom%C3%A9,+Togo&output=embed"
                className="h-[380px] w-full grayscale-[60%] contrast-[1.05]"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default Contact;
